import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

const suporteTecnicoItems = [
  'Análise técnica de até 5 cotações de seguro',
  'Verificação de coberturas, franquias, exclusões e estrutura comercial',
  'Identificação de riscos ocultos e gaps de proteção',
  'Orientação técnica para apoiar a decisão final',
  'Ideal para quem quer aplicar o método por conta própria, com validação especializada',
];

const suportePremiumItems = [
  'Análise completa do perfil de risco',
  'Definição criteriosa das coberturas adequadas',
  'Estratégia de negociação com corretores e seguradoras',
  'Avaliação técnica aprofundada das propostas recebidas',
  'Condução integral do processo, do diagnóstico à contratação final',
];

export default function NiveisSuporte() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: quoteRef, isVisible: quoteVisible } = useScrollAnimation();
  const { ref: textRef, isVisible: textVisible } = useScrollAnimation();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();
  const { ref: card1Ref, visibleItems: vis1 } = useStaggerAnimation(suporteTecnicoItems.length);
  const { ref: card2Ref, visibleItems: vis2 } = useStaggerAnimation(suportePremiumItems.length);

  return (
    <section
      id="niveis-suporte"
      className="relative py-20 sm:py-24 lg:py-32 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0d2a26 0%, #133a33 40%, #0d2a26 100%)' }}
      aria-labelledby="niveis-suporte-heading"
    >
      {/* Padrão de fundo sutil */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, #4ade80 0%, transparent 50%), radial-gradient(circle at 80% 70%, #2dd4bf 0%, transparent 50%)',
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Cabeçalho */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-12 sm:mb-16 transition-all duration-700 ease-out ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Selo */}
          <div className="inline-flex items-center gap-2 border border-teal-500/50 bg-teal-500/10 px-4 py-2 rounded-full mb-6 sm:mb-8">
            <i className="ri-customer-service-2-line text-teal-400 text-sm" aria-hidden="true" />
            <span className="text-teal-300 font-bold text-xs uppercase tracking-widest">
              Suporte Especializado
            </span>
          </div>

          {/* Título */}
          <h2
            id="niveis-suporte-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 sm:mb-6 px-4 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Você pode aplicar o método sozinho —{' '}
            <span className="text-teal-400">ou contar com apoio técnico da IBX Brasil</span>
          </h2>

          {/* Subtítulo */}
          <p className="text-base sm:text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed px-4">
            O método foi criado para funcionar de forma{' '}
            <strong className="text-white">independente</strong>, mas alguns segurados preferem mais segurança
            técnica, validação profissional e menos envolvimento direto no processo.
          </p>
        </div>

        {/* Bloco de destaque central */}
        <div
          ref={quoteRef as React.RefObject<HTMLDivElement>}
          className={`mb-12 sm:mb-16 max-w-3xl mx-auto transition-all duration-700 ease-out delay-150 ${quoteVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="border border-white/15 bg-white/5 backdrop-blur-sm rounded-2xl p-7 sm:p-9 relative">
            {/* Aspas decorativas */}
            <div className="w-10 h-10 flex items-center justify-center bg-teal-500/20 rounded-xl mb-5">
              <i className="ri-double-quotes-l text-teal-400 text-2xl" aria-hidden="true" />
            </div>

            <p className="text-lg sm:text-xl lg:text-2xl text-white font-medium italic leading-relaxed">
              "Você continua no controle da decisão — mas pode escolher o nível de suporte ideal para
              aplicar o método com mais segurança, critério e tranquilidade."
            </p>

            <div className="flex items-center gap-3 mt-6 pt-5 border-t border-white/10">
              <div className="w-1 h-8 bg-teal-400 rounded-full" aria-hidden="true" />
              <div>
                <p className="text-white font-bold text-sm">Método de Contratação Ativa</p>
                <p className="text-gray-400 text-xs">IBX Brasil — Suporte Especializado</p>
              </div>
            </div>
          </div>
        </div>

        {/* Dois cards lado a lado */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16">

          {/* Card 1 — Suporte Técnico */}
          <div
            ref={card1Ref as React.RefObject<HTMLDivElement>}
            className="border border-teal-500/30 bg-white/5 rounded-2xl p-7 sm:p-8 relative overflow-hidden group hover:border-teal-500/60 transition-colors duration-300"
          >
            {/* Badge de nível */}
            <div className="inline-flex items-center gap-1.5 bg-teal-500/15 border border-teal-500/30 px-3 py-1 rounded-full mb-5">
              <div className="w-1.5 h-1.5 rounded-full bg-teal-400" aria-hidden="true" />
              <span className="text-teal-300 text-xs font-bold uppercase tracking-wider">Nível 1</span>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 flex items-center justify-center bg-teal-500/20 rounded-xl shrink-0">
                <i className="ri-tools-line text-teal-400 text-xl" aria-hidden="true" />
              </div>
              <h3
                className="text-white font-bold text-xl sm:text-2xl"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Suporte Técnico
              </h3>
            </div>

            <ul className="space-y-3.5" aria-label="Itens do Suporte Técnico">
              {suporteTecnicoItems.map((item, i) => (
                <li
                  key={i}
                  className={`flex items-start gap-3 transition-all duration-500 ease-out ${vis1[i] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-5'}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-teal-400 text-base" aria-hidden="true" />
                  </div>
                  <span className="text-gray-200 text-sm sm:text-base leading-snug">{item}</span>
                </li>
              ))}
            </ul>

            {/* Linha decorativa inferior */}
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-teal-500/40 to-transparent" aria-hidden="true" />
          </div>

          {/* Card 2 — Suporte Premium */}
          <div
            ref={card2Ref as React.RefObject<HTMLDivElement>}
            className="border border-orange-500/30 bg-white/5 rounded-2xl p-7 sm:p-8 relative overflow-hidden group hover:border-orange-500/60 transition-colors duration-300"
          >
            {/* Badge de nível */}
            <div className="inline-flex items-center gap-1.5 bg-orange-500/15 border border-orange-500/30 px-3 py-1 rounded-full mb-5">
              <div className="w-1.5 h-1.5 rounded-full bg-orange-400" aria-hidden="true" />
              <span className="text-orange-300 text-xs font-bold uppercase tracking-wider">Nível 2</span>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 flex items-center justify-center bg-orange-500/20 rounded-xl shrink-0">
                <i className="ri-vip-crown-line text-orange-400 text-xl" aria-hidden="true" />
              </div>
              <h3
                className="text-white font-bold text-xl sm:text-2xl"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Suporte Premium
              </h3>
            </div>

            <ul className="space-y-3.5" aria-label="Itens do Suporte Premium">
              {suportePremiumItems.map((item, i) => (
                <li
                  key={i}
                  className={`flex items-start gap-3 transition-all duration-500 ease-out ${vis2[i] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-5'}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-arrow-right-circle-fill text-orange-400 text-base" aria-hidden="true" />
                  </div>
                  <span className="text-gray-200 text-sm sm:text-base leading-snug">{item}</span>
                </li>
              ))}
            </ul>

            {/* Linha decorativa inferior */}
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-500/40 to-transparent" aria-hidden="true" />
          </div>
        </div>

        {/* Texto complementar */}
        <p
          ref={textRef as React.RefObject<HTMLParagraphElement>}
          className={`text-center text-gray-400 text-sm sm:text-base max-w-3xl mx-auto leading-relaxed mb-10 sm:mb-12 px-4 transition-all duration-700 ease-out ${textVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          Aplicável para segurados que buscam mais{' '}
          <strong className="text-teal-300">segurança</strong>, economia de tempo,{' '}
          <strong className="text-teal-300">redução de erros</strong> e decisões mais consistentes na
          contratação do seguro.
        </p>

        {/* CTA */}
        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className={`text-center transition-all duration-700 ease-out ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <a
            href="#analise-gratuita"
            className="inline-flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-600 active:scale-95 text-white font-bold text-base sm:text-lg px-8 sm:px-10 py-4 sm:py-5 rounded-xl transition-all duration-200 shadow-lg hover:shadow-orange-500/30 whitespace-nowrap"
            aria-label="Escolher nível de suporte para o Método de Contratação Ativa"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-hand-coin-line text-white text-lg" aria-hidden="true" />
            </div>
            Quero escolher meu nível de suporte
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line text-white text-base" aria-hidden="true" />
            </div>
          </a>
        </div>

      </div>
    </section>
  );
}
